import React from "react";
import { Helmet } from "react-helmet";
import TechIntro from "../ReusableComponents/TechIntro";
import TechWhyChoose from "../ReusableComponents/TechWhyChoose";
import TechRoles from "../ReusableComponents/TechRoles";
import TechStaffingServices from "../ReusableComponents/TechStaffingServices";
import TechRecruitingAgency from "../ReusableComponents/TechRecruitingAgency";
import TechOutcomes from "../ReusableComponents/TechOutcomes";
import TechCTA from "../ReusableComponents/TechCTA";

const roleGroups = [
  {
    title: 'Functional & Finance',
    items: [
      { name: 'Oracle Financials (GL/AP/AR)', icon: '💰' },
      { name: 'Oracle Cloud Fusion HCM', icon: '👥' },
      { name: 'Oracle SCM / Procurement', icon: '📦' },
      { name: 'Oracle Projects (PPM)', icon: '📋' },
    ]
  },
  {
    title: 'Technical & Development',
    items: [
      { name: 'Oracle DBA', icon: '🗄️' },
      { name: 'Oracle APEX Developer', icon: '💻' },
      { name: 'Oracle EBS Technical (RICE)', icon: '⚙️' },
      { name: 'Oracle BI / OBIEE / OAC', icon: '📊' },
    ]
  },
  {
    title: 'Cloud & Infrastructure',
    items: [
      { name: 'OCI Architect', icon: '☁️' },
      { name: 'Oracle Integration Cloud (OIC)', icon: '🔗' },
      { name: 'Oracle Cloud Security', icon: '🔒' },
      { name: 'Oracle CPQ / CX', icon: '📈' },
    ]
  }
];

const features = [
  {
    icon: '✦',
    title: 'Deep Oracle Domain Knowledge',
    text: 'Our recruiters understand the Oracle ecosystem in depth — from EBS R12 to Oracle Cloud Fusion, from Oracle Database 19c to OCI. We match candidates on module, version, and implementation methodology.',
  },
  {
    icon: '✔',
    title: 'Thorough Oracle Vetting',
    text: 'All Oracle consultants are verified for: Oracle certification credentials and hands-on implementation experience, specific module expertise (Financials, SCM, HCM, Procurement, Projects), industry vertical knowledge, and Oracle Cloud Fusion or OCI experience where required.',
  },
  {
    icon: '⚙',
    title: 'Flexible Engagement Models',
    text: 'Contract, project-based, or permanent placement. From a single Oracle Financials consultant to a full Oracle Cloud implementation team — we scale to your project requirements.',
  },
  {
    icon: '🛡',
    title: 'Oracle Upgrade & Migration Specialists',
    text: 'We source consultants experienced in Oracle EBS to Cloud Fusion migrations, database upgrades, and OCI adoption — reducing technical risk on your most critical transformation programs.',
  },
];

const problemItems = [
  'Oracle ERP go-live delays from consultants unfamiliar with your specific modules and business processes.',
  'Data migration failures due to insufficient expertise in Oracle data conversion methodologies.',
  'Budget overruns from rework on poorly configured financial, procurement, or supply chain modules.',
  'Oracle Cloud Fusion migration risk from consultants without OCI or SaaS upgrade experience.',
  'Compliance and audit exposure from poorly implemented Oracle GRC or financial controls.',
];

const solutionPoints = [
  'Oracle-specialist recruiters trained to distinguish between EBS R12, Cloud Fusion, DBA, and OCI roles',
  'Real implementation experience verified before any candidate reaches your desk',
  'Talent matched to your modules, version, and industry for precision delivery',
];

const outcomes = [
  {
    title: 'On-time Oracle go-lives',
    text: 'Certified consultants who execute implementations with precision and deliver on schedule.',
  },
  {
    title: 'Reduced rework and cost overruns',
    text: 'Module experts who configure Oracle correctly the first time.',
  },
  {
    title: 'Smooth Oracle Cloud migrations',
    text: 'Specialists experienced in EBS-to-Fusion migrations who minimize disruption.',
  },
  {
    title: 'Better system performance',
    text: 'Oracle DBAs and architects who optimize your environment for reliability and speed.',
  },
  {
    title: 'Scalable Oracle talent pipeline',
    text: 'A ready bench of pre-screened Oracle professionals aligned to your upgrade roadmap.',
  },
];

const OracleStaffHome = () => {
  return (
    <div>
      <Helmet>
        <title>Oracle Staffing Solutions India | EBS & Cloud Fusion Consultants | Kapil Technologies</title>
        <meta
          name="description"
          content="Kapil Technologies provides certified Oracle staffing solutions. Hire pre-vetted consultants for EBS, Oracle Cloud Fusion, Database, and OCI projects."
        />
        <meta
          name="keywords"
          content="Oracle staffing, Oracle Cloud Fusion consultants, Oracle EBS recruitment, Oracle DBA staffing, OCI architects, Oracle Financials experts, ERP staffing India, Kapil Technologies Oracle"
        />
        <meta name="author" content="Kapil Technologies" />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Oracle Staffing Solutions India | EBS & Cloud Fusion Consultants"
        />
        <meta
          property="og:description"
          content="Certified Oracle staffing solutions. Hire pre-vetted consultants for EBS, Oracle Cloud Fusion, Database, and OCI projects."
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <link
          rel="canonical"
          href="https://kapiltechnologies.com/staff-augmentation/oracle"
        />
      </Helmet>

      {/* Section 1: Hero */}
      <TechIntro 
        videoUrl="https://res.cloudinary.com/djaejg3nn/video/upload/v1781602833/Kapil_Tech_Oracle_Staffing_fke4uj.mp4"
        titleWhite="Certified Oracle Consultants."
        titleGradient="ERP, Cloud & Database Specialists."
      />

      {/* Section 2: Why Choose */}
      <TechWhyChoose 
        title="Struggling to Find Qualified Oracle Professionals?"
        description="Kapil Tech connects enterprises with pre-vetted Oracle consultants across EBS, Oracle Cloud (Fusion), Database, OCI, and industry-specific modules. Our Oracle-focused staffing practice ensures you get specialists who understand your implementation, not just the product name."
        stats={[
          { value: "350+", label: "Oracle Experts Placed" },
          { value: "97%", label: "Client Satisfaction Rate" }
        ]}
        imageUrl="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Technology/Kapil_Tech_Oracle_Staffing.jpg?updatedAt=1781603013821"
        imageAlt="Oracle Staffing by Kapil Tech"
      />

      {/* Section 3: Roles & Specializations */}
      <TechRoles 
        title="Oracle Modules & Roles We Staff"
        description="Our Oracle talent network covers every major functional, technical, and infrastructure domain."
        roleGroups={roleGroups}
      />

      {/* Section 4: Why Enterprises Choose Us */}
      <TechStaffingServices 
        title="Why Enterprises Choose Us for Oracle Staffing"
        description="We combine deep Oracle domain expertise with an agile staffing model to deliver top-tier talent at speed."
        features={features}
      />

      {/* Section 5: Recruiting Agency / Problem-Solution */}
      <TechRecruitingAgency 
        title="Is the Oracle Talent Shortage Blocking Your ERP or Cloud Roadmap?"
        description="Oracle implementations are among the most complex and mission-critical enterprise programs. The market for certified Oracle professionals is extremely tight, and generalist staffing firms routinely send candidates with superficial Oracle exposure — wasting time and derailing timelines on projects where precision is non-negotiable."
        problemTitle="The Real Cost of a Wrong Oracle Hire"
        problemItems={problemItems}
        solutionTitle="Kapil Tech: Your Dedicated Oracle Staffing Partner"
        solutionDescription="Kapil Tech maintains an Oracle-specialist staffing practice with recruiters trained to distinguish between Oracle EBS R12, Oracle Cloud Fusion, Oracle Database DBA, and OCI infrastructure roles. We verify real implementation experience — not just familiarity — before any candidate reaches your desk."
        solutionPoints={solutionPoints}
        ctaText="Find Your Oracle Talent Today"
      />

      {/* Section 6: Business Outcomes */}
      <TechOutcomes 
        title="Business Outcomes You Can Expect"
        description="Partner with Kapil Tech to accelerate your Oracle initiatives and achieve measurable business results through our expert talent solutions."
        outcomes={outcomes}
      />

      {/* Section 7: CTA */}
      <TechCTA 
        title="Ready to Hire Top"
        titleGradient="Oracle Experts?"
        description="Share your Oracle staffing requirements today. Our team responds within hours with certified Oracle professionals matched to your modules and environment."
      />
    </div>
  );
};

export default OracleStaffHome;
